import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  locutorActual,
  programaActual,
  transmisionActual,
  transmisionStatusMeta,
  oyentesActuales,
  mensajes as mensajesIniciales,
  encuestas,
  concursos as concursosIniciales,
  accionesConfig,
  statsCentro,
  reglasCentro,
  tipoMensajeMeta,
  estadoMensajeMeta,
  vistasRapidas,
  filtrarMensajesPorTipo,
  contarPorTipo,
  type MensajeCentro,
  type EncuestaCentro,
  type ConcursoCentro,
} from '@/mocks/centroTransmision';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  return `Hace ${Math.floor(hrs / 24)}d`;
}

export default function CentroTransmisionPage() {
  const [vistaActiva, setVistaActiva] = useState<string>('todo');
  const [mensajes, setMensajes] = useState<MensajeCentro[]>(mensajesIniciales);
  const [concursosState, setConcursosState] = useState<ConcursoCentro[]>(concursosIniciales);
  const [mensajeSeleccionado, setMensajeSeleccionado] = useState<number | null>(null);
  const [respuestaTexto, setRespuestaTexto] = useState<string>('');
  const [mostrarRespuesta, setMostrarRespuesta] = useState<number | null>(null);
  const [mostrarGanador, setMostrarGanador] = useState<number | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const TX = transmisionActual;
  const statusInfo = transmisionStatusMeta[TX.status];

  const mensajesFiltrados = useMemo(
    () => filtrarMensajesPorTipo(mensajes, vistaActiva),
    [mensajes, vistaActiva],
  );

  const concursosActivos = useMemo(
    () => concursosState.filter((c) => c.estado === 'activo'),
    [concursosState],
  );

  const encuestasActivas = useMemo(
    () => encuestas.filter((e) => e.activa),
    [],
  );

  const handleAccion = (tipo: string, msgId?: number) => {
    if (tipo === 'responder' && msgId) {
      setMostrarRespuesta(msgId);
      setRespuestaTexto('');
      return;
    }
    if (tipo === 'destacar' && msgId) {
      setMensajes((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, destacado: !m.destacado, estado: m.destacado ? 'leido' : 'destacado' } : m)),
      );
      const msg = mensajes.find((m) => m.id === msgId);
      if (msg) {
        showToast(msg.destacado ? 'Mensaje quitado de destacados' : 'Mensaje destacado en el muro');
      }
      return;
    }
    if (tipo === 'bloquear' && msgId) {
      setMensajes((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, bloqueado: true, estado: 'bloqueado' } : m)),
      );
      showToast('Usuario bloqueado y mensaje ocultado');
      return;
    }
    if (tipo === 'mover_al_aire' && msgId) {
      setMensajes((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, estado: 'respondido', destacado: true } : m)),
      );
      showToast('¡Mensaje movido al aire! Se leyó en vivo.');
      return;
    }
    if (tipo === 'seleccionar_ganador' && msgId) {
      const concursoActivo = concursosActivos[0];
      if (concursoActivo) {
        setMostrarGanador(concursoActivo.id);
      } else {
        showToast('No hay concursos activos para seleccionar ganador');
      }
      return;
    }
    showToast(`Acción "${tipo}" ejecutada`);
  };

  const handleConfirmarGanador = (concursoId: number) => {
    const participantes = [
      { nombre: 'Lucía Fernández', avatar: 'https://readdy.ai/api/search-image?query=Happy%20young%20Hispanic%20woman%20portrait%2C%20warm%20genuine%20smile%2C%20casual%20modern%20outfit%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20candid%20lifestyle%20portrait%20photography&width=80&height=80&seq=ctx-win-1&orientation=squarish' },
      { nombre: 'Diego Vargas', avatar: 'https://readdy.ai/api/search-image?query=Confident%20young%20Hispanic%20male%20portrait%2C%20friendly%20smile%2C%20casual%20streetwear%20style%2C%20urban%20background%20with%20warm%20sunset%20light%2C%20lifestyle%20portrait%20photography&width=80&height=80&seq=ctx-win-2&orientation=squarish' },
      { nombre: 'Sofía Hernández', avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20trendy%20casual%20style%2C%20bright%20genuine%20smile%2C%20soft%20studio%20light%2C%20clean%20background%2C%20fashion%20portrait%20photography&width=80&height=80&seq=ctx-win-3&orientation=squarish' },
    ];
    const ganador = participantes[Math.floor(Math.random() * participantes.length)];
    setConcursosState((prev) =>
      prev.map((c) =>
        c.id === concursoId
          ? { ...c, estado: 'finalizado' as const, ganador: ganador.nombre, ganadorAvatar: ganador.avatar }
          : c,
      ),
    );
    setMostrarGanador(null);
    showToast(`¡${ganador.nombre} es el ganador! Se anunciará al aire.`);
  };

  const handleEnviarRespuesta = (msgId: number) => {
    if (!respuestaTexto.trim()) return;
    setMensajes((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, estado: 'respondido' } : m)),
    );
    setMostrarRespuesta(null);
    setRespuestaTexto('');
    showToast('Respuesta enviada al oyente');
  };

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const getShortcutKey = (tipo: string): string => {
    const acc = accionesConfig.find((a) => a.tipo === tipo);
    return acc?.shortcut || '';
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* ═══════════ TOP BAR ═══════════ */}
      <header className="sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between h-14 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-arrow-left-line text-foreground-500 text-sm"></i>
            </Link>
            <div className={`w-8 h-8 rounded-lg ${statusInfo.bg} flex items-center justify-center`}>
              <i className={`ri-broadcast-line ${statusInfo.text} text-sm`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-foreground-950 font-bold text-sm">Centro de Transmisión</h1>
                <span className={`w-2 h-2 rounded-full ${statusInfo.dot} ${statusInfo.pulso ? 'animate-pulse' : ''}`}></span>
                <span className={`text-[10px] font-bold ${statusInfo.text}`}>{statusInfo.label}</span>
              </div>
              <p className="text-foreground-400 text-[10px]">
                {locutorActual.nombre} &bull; {programaActual.nombre} &bull; {oyentesActuales.actuales.toLocaleString()} oyentes
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-foreground-400 text-[10px] hidden sm:inline">Uptime: {TX.uptime}</span>
            <Link
              to="/admin/dashboard"
              className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-dashboard-line mr-1"></i>
              Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* ═══════════ SIDEBAR ═══════════ */}
        <aside className="hidden lg:flex flex-col w-[220px] shrink-0 min-h-[calc(100vh-56px)] bg-background-50 border-r border-background-200 sticky top-14">
          <div className="p-3 space-y-1">
            {vistasRapidas.map((vista) => {
              const count = contarPorTipo(vista.id);
              return (
                <button
                  key={vista.id}
                  onClick={() => setVistaActiva(vista.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
                    vistaActiva === vista.id
                      ? 'bg-foreground-950 text-background-50'
                      : 'text-foreground-500 hover:bg-background-100'
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <i className={`${vista.icono} text-sm`}></i>
                    {vista.label}
                  </span>
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                      vistaActiva === vista.id
                        ? 'bg-background-50/20 text-background-50'
                        : 'bg-background-100 text-foreground-500'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Acciones rápidas en sidebar */}
          <div className="mt-4 px-3">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wide px-3 mb-2">Acciones rápidas</p>
            <div className="space-y-1">
              {accionesConfig.map((acc) => (
                <button
                  key={acc.tipo}
                  onClick={() => handleAccion(acc.tipo)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${acc.gradiente} flex items-center justify-center`}>
                    <i className={`${acc.icono} text-background-50 text-[10px]`}></i>
                  </span>
                  {acc.label}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* ═══════════ MAIN CONTENT ═══════════ */}
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-28 lg:pb-8">
            <div className="max-w-[1000px] mx-auto space-y-6">

              {/* Mobile vista tabs */}
              <div className="lg:hidden flex items-center gap-1 overflow-x-auto pb-1 scrollbar-none">
                {vistasRapidas.map((vista) => (
                  <button
                    key={vista.id}
                    onClick={() => setVistaActiva(vista.id)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      vistaActiva === vista.id
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200'
                    }`}
                  >
                    <i className={vista.icono}></i>
                    {vista.label}
                    {contarPorTipo(vista.id) > 0 && (
                      <span className="px-1 py-0.5 rounded-full bg-background-200 text-foreground-400 text-[8px] font-bold">
                        {contarPorTipo(vista.id)}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* ═══════════ METRICS BAR ═══════════ */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { icono: 'ri-user-line', label: 'Oyentes', valor: oyentesActuales.actuales.toLocaleString(), sub: oyentesActuales.porcentaje, color: 'text-emerald-600', bg: 'bg-emerald-100' },
                  { icono: 'ri-chat-3-line', label: 'Sin Leer', valor: mensajes.filter((m) => m.estado === 'nuevo' && !m.bloqueado).length.toString(), sub: `${statsCentro.totalMensajes} hoy`, color: 'text-violet-600', bg: 'bg-violet-100' },
                  { icono: 'ri-trophy-line', label: 'Concursos', valor: concursosActivos.length.toString(), sub: 'Activos', color: 'text-amber-600', bg: 'bg-amber-100' },
                  { icono: 'ri-bar-chart-line', label: 'Encuestas', valor: encuestasActivas.length.toString(), sub: 'Activas', color: 'text-sky-600', bg: 'bg-sky-100' },
                  { icono: 'ri-timer-line', label: 'Resp. Promedio', valor: statsCentro.tiempoPromedioRespuesta, sub: 'Meta: &lt;5m', color: 'text-rose-600', bg: 'bg-rose-100' },
                  { icono: 'ri-heart-line', label: 'Satisfacción', valor: statsCentro.satisfaccionOyente, sub: 'Oyentes', color: 'text-teal-600', bg: 'bg-teal-100' },
                ].map((metric) => (
                  <div key={metric.label} className="bg-background-50 rounded-2xl border border-background-200 p-3">
                    <div className={`w-7 h-7 rounded-lg ${metric.bg} flex items-center justify-center mb-2`}>
                      <i className={`${metric.icono} ${metric.color} text-xs`}></i>
                    </div>
                    <p className="text-foreground-400 text-[10px]">{metric.label}</p>
                    <p className="text-foreground-950 font-bold text-base">{metric.valor}</p>
                    <p className="text-foreground-400 text-[9px]">{metric.sub}</p>
                  </div>
                ))}
              </div>

              {/* ═══════════ LIVE STATUS BAR ═══════════ */}
              <div className="bg-background-50 rounded-2xl border-2 border-emerald-300 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={locutorActual.avatar}
                      alt={locutorActual.nombre}
                      className="w-12 h-12 rounded-2xl object-cover shrink-0"
                      loading="lazy"
                    />
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse`}></span>
                        <span className="text-emerald-700 font-bold text-xs">EN VIVO</span>
                        <span className="text-foreground-400 text-[10px]">&bull; {TX.uptime}</span>
                      </div>
                      <p className="text-foreground-950 font-bold text-sm">{locutorActual.nombre}</p>
                      <p className="text-foreground-500 text-xs">{programaActual.nombre} &bull; {TX.formato} {TX.bitrate}kbps</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-foreground-950 font-bold text-xl">{oyentesActuales.actuales.toLocaleString()}</p>
                      <p className="text-foreground-400 text-[9px]">Oyentes</p>
                    </div>
                    <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
                    <div className="text-center">
                      <p className="text-foreground-950 font-bold text-xl">{oyentesActuales.enCabina}</p>
                      <p className="text-foreground-400 text-[9px]">En Cabina</p>
                    </div>
                    <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
                    <div className="hidden sm:block">
                      <div className="h-2 bg-background-200 rounded-full w-32 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${programaActual.progreso}%` }}
                        ></div>
                      </div>
                      <p className="text-foreground-400 text-[9px] mt-1 text-right">{programaActual.tiempoRestante} restante</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══════════ CONTENT: MENSAJES ═══════════ */}
              {(vistaActiva === 'todo' || vistaActiva === 'mensajes' || vistaActiva === 'saludos' || vistaActiva === 'canciones' || vistaActiva === 'concursos' || vistaActiva === 'bloqueados') && (
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                      <i className="ri-inbox-line text-accent-500"></i>
                      {vistaActiva === 'todo' ? 'Todos los mensajes' :
                       vistaActiva === 'bloqueados' ? 'Mensajes bloqueados' :
                       `Mensajes de ${vistaActiva}`}
                    </h3>
                    <span className="text-foreground-400 text-[10px]">{mensajesFiltrados.length} mensajes</span>
                  </div>

                  {mensajesFiltrados.length === 0 ? (
                    <div className="bg-background-50 rounded-2xl border border-background-200 p-8 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-inbox-line text-foreground-300 text-2xl"></i>
                      </div>
                      <p className="text-foreground-500 text-sm font-semibold">No hay mensajes en esta vista</p>
                      <p className="text-foreground-400 text-xs">Seleccioná otra categoría para ver más contenido.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {mensajesFiltrados.map((msg) => {
                        const tipoMeta = tipoMensajeMeta[msg.tipo];
                        const estadoMeta = estadoMensajeMeta[msg.estado];
                        const isSelected = mensajeSeleccionado === msg.id;
                        return (
                          <div
                            key={msg.id}
                            onClick={() => setMensajeSeleccionado(isSelected ? null : msg.id)}
                            className={`bg-background-50 rounded-2xl border-2 transition-all cursor-pointer ${
                              isSelected ? 'border-accent-500' : 'border-background-200 hover:border-background-300'
                            } ${msg.destacado ? 'ring-1 ring-amber-300' : ''}`}
                          >
                            <div className="p-4">
                              <div className="flex items-start gap-3">
                                <img
                                  src={msg.avatar}
                                  alt={msg.remitente}
                                  className="w-10 h-10 rounded-xl object-cover shrink-0"
                                  loading="lazy"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-foreground-950 font-bold text-xs">{msg.remitente}</span>
                                    <span className={`px-1.5 py-0.5 rounded-md ${tipoMeta.color} text-[9px] font-semibold`}>
                                      <i className={`${tipoMeta.icono} mr-0.5`}></i>
                                      {tipoMeta.label}
                                    </span>
                                    {msg.destacado && (
                                      <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold">
                                        <i className="ri-star-fill mr-0.5"></i>Destacado
                                      </span>
                                    )}
                                    {msg.bloqueado && (
                                      <span className="px-1.5 py-0.5 rounded-md bg-rose-100 text-rose-700 text-[9px] font-semibold">
                                        <i className="ri-forbid-2-line mr-0.5"></i>Bloqueado
                                      </span>
                                    )}
                                    <span className={`px-1.5 py-0.5 rounded-md ${estadoMeta.bg} ${estadoMeta.text} text-[9px] font-semibold`}>
                                      {estadoMeta.label}
                                    </span>
                                  </div>
                                  <p className="text-foreground-600 text-xs leading-relaxed">{msg.contenido}</p>
                                  {msg.cancionSolicitada && (
                                    <p className="text-violet-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                                      <i className="ri-music-fill"></i>{msg.cancionSolicitada}
                                    </p>
                                  )}
                                  {msg.respuestaConcurso && (
                                    <p className="text-amber-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                                      <i className="ri-trophy-fill"></i>Respuesta: {msg.respuestaConcurso}
                                    </p>
                                  )}
                                  {msg.duracionVoz && (
                                    <p className="text-rose-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                                      <i className="ri-mic-fill"></i>Duración: {msg.duracionVoz}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className="text-foreground-400 text-[10px]">{timeAgo(msg.timestamp)}</span>
                                    <span className="text-foreground-300 text-[10px]">{msg.ubicacion}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Action buttons (show when selected) */}
                              {isSelected && !msg.bloqueado && (
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200 flex-wrap">
                                  {accionesConfig.map((acc) => (
                                    <button
                                      key={acc.tipo}
                                      onClick={(e) => { e.stopPropagation(); handleAccion(acc.tipo, msg.id); }}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors whitespace-nowrap bg-gradient-to-r ${acc.gradiente} text-background-50 hover:opacity-90`}
                                    >
                                      <i className={acc.icono}></i>
                                      {acc.label}
                                    </button>
                                  ))}
                                </div>
                              )}

                              {/* Reply modal */}
                              {mostrarRespuesta === msg.id && (
                                <div className="mt-3 pt-3 border-t border-background-200" onClick={(e) => e.stopPropagation()}>
                                  <textarea
                                    value={respuestaTexto}
                                    onChange={(e) => setRespuestaTexto(e.target.value)}
                                    placeholder="Escribí tu respuesta..."
                                    maxLength={500}
                                    className="w-full px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-800 text-xs resize-none outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500/20 transition-colors h-20"
                                  ></textarea>
                                  <div className="flex items-center justify-between mt-2">
                                    <span className="text-foreground-400 text-[9px]">{respuestaTexto.length}/500</span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => setMostrarRespuesta(null)}
                                        className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-background-200 transition-colors"
                                      >
                                        Cancelar
                                      </button>
                                      <button
                                        onClick={() => handleEnviarRespuesta(msg.id)}
                                        disabled={!respuestaTexto.trim()}
                                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-colors whitespace-nowrap ${
                                          respuestaTexto.trim()
                                            ? 'bg-emerald-500 text-background-50 hover:bg-emerald-600'
                                            : 'bg-background-100 text-foreground-300 cursor-not-allowed'
                                        }`}
                                      >
                                        <i className="ri-send-plane-fill mr-1"></i>
                                        Enviar
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* ═══════════ ENCUESTAS ═══════════ */}
              {vistaActiva === 'encuestas' && (
                <section>
                  <h3 className="text-foreground-950 font-bold text-sm mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-accent-500"></i>
                    Encuestas
                  </h3>
                  <div className="space-y-4">
                    {encuestas.map((encuesta) => (
                      <div key={encuesta.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="text-foreground-950 font-bold text-sm">{encuesta.pregunta}</h4>
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${
                            encuesta.activa ? 'bg-emerald-100 text-emerald-700' : 'bg-foreground-100 text-foreground-500'
                          }`}>
                            {encuesta.activa ? 'Activa' : 'Cerrada'}
                          </span>
                        </div>
                        <div className="space-y-2.5">
                          {encuesta.opciones.map((op, idx) => (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-foreground-600 text-xs">{op.texto}</span>
                                <span className="text-foreground-400 text-[10px]">{op.votos} votos ({op.porcentaje}%)</span>
                              </div>
                              <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all bg-gradient-to-r from-accent-500 to-primary-500"
                                  style={{ width: `${op.porcentaje}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200">
                          <span className="text-foreground-400 text-[10px]">{encuesta.totalVotos.toLocaleString()} votos totales</span>
                          <span className="text-foreground-400 text-[10px]">
                            {encuesta.activa ? `Cierra ${timeAgo(encuesta.cierra)}` : 'Finalizada'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ═══════════ CONCURSOS ═══════════ */}
              {(vistaActiva === 'todo' || vistaActiva === 'concursos') && concursosState.length > 0 && (
                <section>
                  <h3 className="text-foreground-950 font-bold text-sm mb-4 flex items-center gap-2">
                    <i className="ri-trophy-line text-accent-500"></i>
                    Concursos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {concursosState.map((concurso) => (
                      <div key={concurso.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-foreground-950 font-bold text-sm">{concurso.titulo}</h4>
                            <p className="text-foreground-500 text-[11px] mt-0.5">{concurso.premio}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold whitespace-nowrap ${
                            concurso.estado === 'activo' ? 'bg-emerald-100 text-emerald-700' :
                            concurso.estado === 'finalizado' ? 'bg-foreground-100 text-foreground-500' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {concurso.estado === 'activo' ? 'Activo' : concurso.estado === 'finalizado' ? 'Finalizado' : 'Próximamente'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="bg-background-100 rounded-xl p-3">
                            <p className="text-foreground-400 text-[10px]">Participantes</p>
                            <p className="text-foreground-950 font-bold text-lg">{concurso.participantes.toLocaleString()}</p>
                          </div>
                          <div className="bg-background-100 rounded-xl p-3">
                            <p className="text-foreground-400 text-[10px]">Resp. Correctas</p>
                            <p className="text-foreground-950 font-bold text-lg">{concurso.respuestasCorrectas.toLocaleString()}</p>
                          </div>
                        </div>
                        {concurso.ganador && (
                          <div className="flex items-center gap-2 bg-amber-50 rounded-xl p-3">
                            <img
                              src={concurso.ganadorAvatar}
                              alt={concurso.ganador}
                              className="w-8 h-8 rounded-lg object-cover"
                              loading="lazy"
                            />
                            <div>
                              <p className="text-amber-800 font-bold text-[10px]">GANADOR</p>
                              <p className="text-amber-700 text-xs">{concurso.ganador}</p>
                            </div>
                          </div>
                        )}
                        {concurso.estado === 'activo' && (
                          <button
                            onClick={() => handleAccion('seleccionar_ganador', concurso.id)}
                            className="w-full mt-3 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-background-50 text-xs font-bold cursor-pointer hover:from-amber-600 hover:to-yellow-600 transition-colors whitespace-nowrap"
                          >
                            <i className="ri-trophy-line mr-1"></i>
                            Seleccionar Ganador
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ═══════════ REGLAS ═══════════ */}
              <section>
                <h3 className="text-foreground-950 font-bold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-scales-line text-accent-500"></i>
                  Reglas del Centro de Transmisión
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {reglasCentro.map((regla) => (
                    <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                          <i className={`${regla.icono} text-background-50 text-sm`}></i>
                        </div>
                        <div>
                          <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                          <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

            </div>
          </div>
        </main>
      </div>

      {/* ═══════════ WINNER SELECTION MODAL ═══════════ */}
      {mostrarGanador !== null && (() => {
        const concurso = concursosState.find((c) => c.id === mostrarGanador);
        if (!concurso) return null;
        const participantesPreview = [
          { nombre: 'Lucía Fernández', avatar: 'https://readdy.ai/api/search-image?query=Happy%20young%20Hispanic%20woman%20portrait%2C%20warm%20genuine%20smile%2C%20casual%20modern%20outfit%2C%20natural%20light%2C%20soft%20bokeh%20background%2C%20candid%20lifestyle%20portrait%20photography&width=80&height=80&seq=ctx-win-m1&orientation=squarish' },
          { nombre: 'Diego Vargas', avatar: 'https://readdy.ai/api/search-image?query=Confident%20young%20Hispanic%20male%20portrait%2C%20friendly%20smile%2C%20casual%20streetwear%20style%2C%20urban%20background%20with%20warm%20sunset%20light%2C%20lifestyle%20portrait%20photography&width=80&height=80&seq=ctx-win-m2&orientation=squarish' },
          { nombre: 'Sofía Hernández', avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20a%20young%20Latina%20woman%2C%20trendy%20casual%20style%2C%20bright%20genuine%20smile%2C%20soft%20studio%20light%2C%20clean%20background%2C%20fashion%20portrait%20photography&width=80&height=80&seq=ctx-win-m3&orientation=squarish' },
        ];
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setMostrarGanador(null)}>
            <div className="bg-background-50 rounded-2xl border border-background-200 p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-trophy-line text-background-50 text-2xl"></i>
                </div>
                <h3 className="text-foreground-950 font-bold text-base">Seleccionar Ganador</h3>
                <p className="text-foreground-500 text-xs mt-1">{concurso.titulo}</p>
              </div>
              <p className="text-foreground-500 text-xs mb-4 text-center">
                Se seleccionará un ganador aleatorio entre {concurso.participantes.toLocaleString()} participantes con {concurso.respuestasCorrectas.toLocaleString()} respuestas correctas.
              </p>
              <div className="flex items-center gap-3 mb-5">
                {participantesPreview.map((p, idx) => (
                  <div key={idx} className="flex-1 text-center">
                    <img src={p.avatar} alt={p.nombre} className="w-14 h-14 rounded-2xl object-cover mx-auto mb-1" loading="lazy" />
                    <p className="text-foreground-600 text-[10px] font-semibold">{p.nombre.split(' ')[0]}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMostrarGanador(null)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleConfirmarGanador(concurso.id)}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-background-50 text-xs font-bold cursor-pointer hover:from-amber-600 hover:to-yellow-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shuffle-line mr-1"></i>
                  Seleccionar Aleatorio
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════ TOAST ═══════════ */}
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold shadow-lg animate-bounce">
          {toastMsg}
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
        <div className="flex items-center justify-around h-[56px]">
          {accionesConfig.slice(0, 4).map((acc) => (
            <button
              key={acc.tipo}
              onClick={() => handleAccion(acc.tipo)}
              className="flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer text-foreground-400 transition-colors"
            >
              <i className={`${acc.icono} text-lg`}></i>
              <span className="text-[9px] font-semibold">{acc.label}</span>
            </button>
          ))}
          <Link
            to="/admin/dashboard"
            className="flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer text-foreground-400 transition-colors"
          >
            <i className="ri-dashboard-line text-lg"></i>
            <span className="text-[9px] font-semibold">Dashboard</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}